import {skipToken} from '@reduxjs/toolkit/query';
import {FormControl, FormField} from 'platform/components';
import {Grid} from 'platform/foundation';

import {useWatch, UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {
  useGetMazdaWarrantyAssemblyGroupsQuery,
  useGetMazdaWarrantyDamagesQuery,
  useGetMazdaWarrantyLocationsQuery,
  useGetMazdaWarrantySubAssemblyGroupsQuery,
  useGetMazdaWarrantySymptomsQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getMazdaCatalogOptions} from '../../../../../utils/getMazdaCatalogOptions';
import {
  MazdaAdditionalInformationFormType,
  PNC_CODE_MAX_LENGTH,
} from '../schemas/mazdaAdditionalInformationFormSchema';
import {MazdaDtcCodeList} from './MazdaDtcCodeList';

interface MazdaInformationFormFieldsProps extends TestIdProps {
  control: FormControl<MazdaAdditionalInformationFormType>;
  formApi: UseFormReturn<MazdaAdditionalInformationFormType>;
  isReadOnly: boolean;
}

export function MazdaInformationFormFields(props: MazdaInformationFormFieldsProps) {
  const [assemblyGroup, subAssemblyGroup] = useWatch({
    control: props.formApi.control,
    name: ['assemblyGroup', 'subAssemblyGroup'],
  });

  const isAssemblyGroupSelected = isNotNil(assemblyGroup);
  const isSubAssemblyGroupSelected = isNotNil(subAssemblyGroup);

  const assemblyGroupArg = isAssemblyGroupSelected ? {assemblyGroupCode: assemblyGroup} : skipToken;

  const {data: assemblyGroups} = useGetMazdaWarrantyAssemblyGroupsQuery();
  const {data: subAssemblyGroups} = useGetMazdaWarrantySubAssemblyGroupsQuery(assemblyGroupArg);
  const {data: symptoms} = useGetMazdaWarrantySymptomsQuery(assemblyGroupArg);
  const {data: damages} = useGetMazdaWarrantyDamagesQuery(assemblyGroupArg);
  const {data: locations} = useGetMazdaWarrantyLocationsQuery(
    isAssemblyGroupSelected && isSubAssemblyGroupSelected
      ? {assemblyGroupCode: assemblyGroup, subAssemblyGroupCode: subAssemblyGroup}
      : skipToken
  );

  const handleAssemblyGroupChange = () => {
    props.formApi.setValue('subAssemblyGroup', null);
    props.formApi.setValue('symptomCode', null);
    props.formApi.setValue('damageCode', null);
    props.formApi.setValue('locationCodes', null);
  };

  const handleSubAssemblyGroupChange = () => {
    props.formApi.setValue('locationCodes', null);
  };

  return (
    <Grid columns={3} spacing={4}>
      <FormField
        control={props.control}
        name="assemblyGroup"
        type="choice"
        options={getMazdaCatalogOptions(assemblyGroups?.assemblyGroups)}
        label={i18n.t('entity.serviceOrder.labels.assemblyGroup')}
        placeholder={i18n.t('general.labels.select')}
        onChange={handleAssemblyGroupChange}
        isDisabled={props.isReadOnly}
        data-testid={suffixTestId('assemblyGroup', props)}
      />
      <FormField
        control={props.control}
        name="subAssemblyGroup"
        type="choice"
        options={getMazdaCatalogOptions(subAssemblyGroups?.subAssemblyGroups)}
        label={i18n.t('entity.serviceOrder.labels.subAssemblyGroup')}
        placeholder={i18n.t('general.labels.select')}
        onChange={handleSubAssemblyGroupChange}
        isDisabled={props.isReadOnly || !isAssemblyGroupSelected}
        data-testid={suffixTestId('subAssemblyGroup', props)}
      />
      <FormField
        control={props.control}
        name="pncCode"
        type="text"
        label={i18n.t('entity.serviceOrder.labels.pncCode')}
        maxLength={PNC_CODE_MAX_LENGTH}
        isCounterVisible
        isDisabled={props.isReadOnly}
        data-testid={suffixTestId('pncCode', props)}
      />
      <FormField
        control={props.control}
        name="symptomCode"
        type="choice"
        options={getMazdaCatalogOptions(symptoms?.symptoms)}
        label={i18n.t('entity.serviceOrder.labels.symptomCode')}
        placeholder={i18n.t('general.labels.select')}
        isDisabled={props.isReadOnly || !isAssemblyGroupSelected}
        data-testid={suffixTestId('symptomCode', props)}
      />
      <FormField
        control={props.control}
        name="damageCode"
        type="choice"
        options={getMazdaCatalogOptions(damages?.damages)}
        label={i18n.t('entity.serviceOrder.labels.damage')}
        placeholder={i18n.t('general.labels.select')}
        isDisabled={props.isReadOnly || !isAssemblyGroupSelected}
        data-testid={suffixTestId('damageCode', props)}
      />
      <FormField
        control={props.control}
        name="locationCodes"
        type="multiChoice"
        options={getMazdaCatalogOptions(locations?.locations)}
        label={i18n.t('entity.serviceOrder.labels.locationCode')}
        placeholder={i18n.t('general.labels.select')}
        isDisabled={props.isReadOnly || !isSubAssemblyGroupSelected}
        data-testid={suffixTestId('locationCodes', props)}
      />
      <MazdaDtcCodeList
        control={props.control}
        isDisabled={props.isReadOnly}
        data-testid={suffixTestId('dtcCodes', props)}
      />
    </Grid>
  );
}
